/// <amd-module name="Feed/Legacy/Helpers/CreateFeedHelper" />
import { IVssPageContext } from "VSS/Platform/Context";
import { Identity } from "VSS/Platform/Contracts/Identities";

import { Feed, FeedPermission, FeedRole, FeedView, FeedVisibility } from "Feed/Common/Contracts/Feed";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";
import { IdentityDataService } from "Feed/Legacy/DataServices/IdentityDataService";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { filterDefaultUpstreamSources } from "Feed/Legacy/Helpers/UpstreamHelper";

export class Sources {
    public static public = "public";
    public static local = "local";
}

export class Visibility {
    public static aadTenant = "aadTenant";
    public static collection = "collection";

    /**
     * This is a legacy value used only if upstreams are disabled, which
     * should never be the case.
     */
    public static defaultProjCollValidUsers = "account";
    public static private = "private";
    public static public = "public";
}

export interface ICreateFeedSettings {
    projectName: string;
    projectId: string;
    feedName: string;
    sources: string;
    visibility: string;
}

class SetFeedVisibilityHelper {
    public async SetFeedVisibility(pageContext: IVssPageContext, feed: Feed, visibility: string): Promise<Feed> {
        // Do nothing if visibility is set to Private
        if (visibility === Visibility.private) {
            return feed;
        }

        const feedsDataService = pageContext.getService<IFeedDataService>("IFeedDataService");
        const webPageDataService = pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");

        // If visibility is set to the default (upstreams are disabled), add the everyone group to the default View
        if (visibility === Visibility.defaultProjCollValidUsers || visibility === Visibility.public) {
            return feedsDataService
                .setFeedViewPermissionsAsync(feed, feed.defaultViewId, [
                    {
                        identityDescriptor: webPageDataService.getEveryoneGroup(),
                        identityId: undefined,
                        role: FeedRole.Reader
                    } as FeedPermission
                ])
                .then(() => feed);
        }

        if (visibility === Visibility.collection || visibility === Visibility.aadTenant) {
            const visibilityEnum =
                visibility === Visibility.aadTenant
                    ? FeedVisibility.AadTenant
                    : visibility === Visibility.collection
                    ? FeedVisibility.Collection
                    : FeedVisibility.Private;
            const viewPatch: FeedView = { visibility: visibilityEnum } as FeedView;
            return feedsDataService
                .updateFeedViewAsync(
                    null /* ProjectId null because for Q1 creating project scoped feed through UI is not enabled */,
                    feed.id,
                    viewPatch,
                    feed.defaultViewId
                )
                .then(feedView => feed);
        }
    }

    public async GetFeedWithPermissions(pageContext: IVssPageContext, feed: Feed): Promise<Feed> {
        const feedsDataService = pageContext.getService<IFeedDataService>("IFeedDataService");
        return feedsDataService.getFeedPermissionsAsync(feed).then(permissions => {
            feed.permissions = permissions;
            return feed;
        });
    }
}

export class CreateFeedHelper {
    public async ConstructFeed(pageContext: IVssPageContext, feedSettings: ICreateFeedSettings): Promise<Feed> {
        const feed = {} as Feed;

        const webPageDataService = pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");

        // Name
        feed.name = feedSettings.feedName;
        feed.hideDeletedPackageVersions = true;

        // Permissions
        // Project Collection Build Service (collection)
        feed.permissions = [
            {
                identityDescriptor: webPageDataService.getCollectionBuildIdentity().descriptor,
                identityId: undefined,
                role: FeedRole.Contributor
            } as FeedPermission
        ];

        // Project Build Service (project)
        const projectBuildIdentity = webPageDataService.getProjectBuildIdentity();
        if (projectBuildIdentity && projectBuildIdentity.descriptor) {
            feed.permissions.push({
                identityDescriptor: projectBuildIdentity.descriptor,
                identityId: undefined,
                role: FeedRole.Contributor
            } as FeedPermission);
        }

        // Upstream sources
        if (feedSettings.sources === Sources.public) {
            feed.upstreamEnabled = true;
            feed.upstreamSources = filterDefaultUpstreamSources(pageContext);
        }

        // If visibility is set to the default (upstreams are disabled), add the everyone group to feed permissions
        // Placed here in case both upstreams flags are off
        if (
            feedSettings.visibility === Visibility.defaultProjCollValidUsers ||
            feedSettings.visibility === Visibility.public
        ) {
            feed.permissions.push({
                identityDescriptor: webPageDataService.getEveryoneGroup(),
                identityId: undefined,
                role: FeedRole.Reader
            } as FeedPermission);
        }

        // If we are in a project, get the Project's groups
        if (feedSettings.projectId != null) {
            const identityDataService = pageContext.getService<IdentityDataService>("IFeedIdentityDataService");

            // Set project to request only if it's a public project because upstream sources are not allowed on project feeds for now.
            if (feedSettings.visibility == Visibility.public) {
                feed.project = {
                    id: feedSettings.projectId,
                    name: feedSettings.projectName,
                    visibility: feedSettings.visibility
                };
            }

            return identityDataService.listGroups(feedSettings.projectId).then((groups: Identity[]) => {
                // Project Administrators
                const projectAdministratorsGroup = groups.filter(
                    i => i.properties.Account.$value === "Project Administrators"
                );
                projectAdministratorsGroup.forEach(identity => {
                    feed.permissions.push({
                        identityDescriptor: identity.descriptor,
                        identityId: undefined,
                        role: FeedRole.Administrator
                    } as FeedPermission);
                });

                // Project Contributors
                const projectContributorsGroup = groups.filter(i => i.properties.Account.$value === "Contributors");
                projectContributorsGroup.forEach(identity => {
                    feed.permissions.push({
                        identityDescriptor: identity.descriptor,
                        identityId: undefined,
                        role: FeedRole.Contributor
                    } as FeedPermission);
                });

                return feed;
            });
        }

        return feed;
    }

    public async SaveFeed(pageContext: IVssPageContext, feedSettings: ICreateFeedSettings, feed: Feed): Promise<Feed> {
        const feedsDataService = pageContext.getService<IFeedDataService>("IFeedDataService");
        const projectId = feed.project ? feed.project.id : null;
        const createdFeed = await feedsDataService.createFeedAsync(projectId, feed);
        return createdFeed;
    }
}

export function createFeed(pageContext: IVssPageContext, feedSettings: ICreateFeedSettings): Promise<Feed> {
    const createFeedHelper = new CreateFeedHelper();
    return createFeedHelper.ConstructFeed(pageContext, feedSettings).then(constructedFeed => {
        return createFeedHelper.SaveFeed(pageContext, feedSettings, constructedFeed);
    });
}

export function setFeedVisibility(pageContext: IVssPageContext, feed: Feed, visibility: string): Promise<Feed> {
    const visHelper = new SetFeedVisibilityHelper();
    return visHelper.SetFeedVisibility(pageContext, feed, visibility).then(feedWithVisibilitySet => {
        return visHelper.GetFeedWithPermissions(pageContext, feedWithVisibilitySet);
    });
}
